import { Link } from "react-router-dom";
import { Seo } from "../lib/seo";
import { PilotTimeline } from "../components/PilotTimeline";
import { AssistantArchitectureDiagram } from "../components/AssistantArchitectureDiagram";

const differentiators = [
  {
    title: "MCP-native and bank2ai-aligned",
    text: "Runs against any compliant bank2ai server: Bancony Server, one you've built yourself, or one from another vendor. The bank is never locked into Bancony's runtime to keep its assistant alive.",
  },
  {
    title: "Generative UI inside the banking channel",
    text: "Bank-grade transaction confirmations, account snapshots, transfer previews, and spending charts render natively in the bank's app. Not chat-bubble prose. Real banking UI, generated to the question.",
  },
  {
    title: "Drop-in via iframe (web) or WebView (mobile)",
    text: "The authenticated user's security context passes through from the bank app to the Assistant to the Server to bank APIs. No new auth surface. No new perimeter to defend.",
  },
  {
    title: "Federated learning, isolated data",
    text: "Customer data stays on bank infrastructure. Anonymised signal (PII, account numbers, and identifiers masked) flows to our hosted Langfuse for cross-bank improvement. Every bank on Bancony gets better as the network grows. We call this Bancony Federated Learning.",
  },
];

export const Assistant = () => (
  <>
    <Seo
      title="Bancony Assistant"
      description="The AI assistant for digital banking. Live in weeks, compliant on day one, in-channel by design."
      path="/assistant"
    />

    <section className="page-hero">
      <div className="page-hero-inner">
        <span className="section-eyebrow">Bancony Assistant</span>
        <h1>AI assistant for digital banking. Live in weeks, not quarters.</h1>
        <p className="page-hero-sub">
          The in-channel AI experience that ships fast and lands compliant on day one.
          Drops into your existing web and mobile banking, inherits your customer's
          session, and writes nothing your regulator wouldn't accept.
        </p>
        <p className="page-hero-sub" style={{ marginTop: 8, fontSize: 16, color: "rgba(255,255,255,0.62)" }}>
          For retail customers, this is the first step toward conversational banking.
          The expectation that "I should be able to just ask" is already forming. The
          banks that meet it inside their own app, in their own brand, keep the
          relationship as that expectation hardens.
        </p>
        <div className="home-hero-ctas">
          <Link to="/contact?intent=assistant" className="btn btn-primary">Book a pilot scoping call</Link>
          <a
            href="https://demo.bancony.com/#assistant"
            target="_blank"
            rel="noopener noreferrer"
            className="btn btn-ghost"
          >
            Try it in the demo bank
          </a>
          <Link to="/server" className="btn btn-ghost">See Bancony Server</Link>
        </div>
      </div>
    </section>

    <section className="section">
      <div className="section-inner">
        <span className="section-eyebrow">The four-week pilot promise</span>
        <h2>From scoping call to internal pilot in 4 weeks.</h2>
        <p className="lede">
          6 to 8 weeks to a live customer pilot is the realistic case for most banks.
          The schedule below assumes executive sponsorship, a defined initial use case,
          and the bank's integration team is aligned.
        </p>
        <PilotTimeline />
      </div>
    </section>

    <section className="section dark">
      <div className="section-inner">
        <span className="section-eyebrow">What makes it different</span>
        <h2>Four things you can't get from a generic chatbot.</h2>
        <p className="lede">
          Citation-grounded RAG, escalation to a human, and brand customisation are
          table stakes; see below. These four are not.
        </p>

        <div className="differentiator-list" style={{ marginTop: 36 }}>
          {differentiators.map((d) => (
            <div className="differentiator" key={d.title}>
              <h3>{d.title}</h3>
              <p>{d.text}</p>
            </div>
          ))}
        </div>
      </div>
    </section>

    <section className="section">
      <div className="section-inner">
        <span className="section-eyebrow">Table-stakes features</span>
        <h2>Briefly, because they're expected.</h2>
        <p className="lede" style={{ maxWidth: 760 }}>
          Citation-grounded RAG over the bank's knowledge base (fee schedules, product
          terms, FAQs). User memory with explicit GDPR Article 6/7 consent, and the bank
          can offer the customer a setting to manage what's remembered. Graceful
          escalation to a human agent with full conversation context handoff. Bank-branded
          UI customised to the bank's design system; end customers see the bank, not
          Bancony.
        </p>
      </div>
    </section>

    <section className="section soft">
      <div className="section-inner">
        <span className="section-eyebrow">Compliance by construction</span>
        <h2>What the bank gets, free, on the EU AI Act.</h2>

        <div className="regulation-split" style={{ marginTop: 24 }}>
          <div className="regulation-block">
            <h4>What ships with Assistant</h4>
            <ul>
              <li><strong>Article 50 transparency</strong>: clear AI disclosure built into the channel UI. In force since 2 August 2026.</li>
              <li><strong>Article 12-15 traceability</strong>: every conversation logged with model+version, prompt template hash, retrieved documents, tool calls, consent state, and outputs.</li>
              <li><strong>Article 14 human oversight</strong>: human-in-the-loop primitives for any destructive or irreversible action.</li>
              <li><strong>DPIA template</strong>: ready for the bank's DPO to adapt.</li>
            </ul>
          </div>
          <div className="regulation-block">
            <h4>What the bank still owns</h4>
            <ul>
              <li>Risk classification of specific use cases. Most land below the high-risk threshold; Annex III 5(b) catches creditworthiness evaluation and credit scoring.</li>
              <li>Notifying its regulator if and when scope warrants.</li>
              <li>Conformity assessment for any high-risk AI system the bank operates, against the 2 December 2027 date.</li>
              <li>Article 4 AI literacy training for staff (we provide the materials).</li>
            </ul>
          </div>
        </div>

        <p style={{ marginTop: 28, color: "var(--ink-mute)", maxWidth: 760 }}>
          Article 50 is the duty that binds a customer-facing assistant today. Articles
          12-15 and 14 are Chapter III obligations that apply to high-risk systems from
          2 December 2027; Assistant ships them now so a later change of scope does not
          mean a rebuild.
        </p>

        <p style={{ marginTop: 20 }}>
          <Link to="/compliance" className="btn-arrow">See the full compliance picture</Link>
        </p>
      </div>
    </section>

    <section className="section">
      <div className="section-inner">
        <span className="section-eyebrow">Deployment options</span>
        <h2>Same three modes as Server.</h2>
        <p className="lede" style={{ maxWidth: 760 }}>
          On-prem, bank cloud, or Bancony SaaS. Langfuse is hosted by Bancony by default
          so Federated Learning works across the network, but the bank can also
          self-host Langfuse for single-bank mode, with no shared improvements.
        </p>
      </div>
    </section>

    <section className="section soft">
      <div className="section-inner">
        <span className="section-eyebrow">Architecture</span>
        <h2>How the pieces fit together.</h2>
        <AssistantArchitectureDiagram />
      </div>
    </section>

    <section className="section">
      <div className="section-inner">
        <div className="final-cta">
          <div>
            <h2>Scope a four-week pilot.</h2>
            <p>30 minutes. We'll cover the use case, the auth integration, and the path to a live customer pilot.</p>
          </div>
          <div className="final-cta-cta">
            <Link to="/contact?intent=assistant" className="btn btn-primary">Book a scoping call</Link>
          </div>
        </div>
      </div>
    </section>
  </>
);
